//Dependencies............................................
import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  InputRef,
  Popover,
  Space,
  Spin,
  Table,
  TableColumnType,
  Tag,
  Tooltip,
  TableColumnsType,
  Typography,
  notification
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SearchOutlined,
  SendOutlined,
  SettingOutlined,
  UpSquareOutlined,
} from "@ant-design/icons";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterDropdownProps, SorterResult, TablePaginationConfig } from "antd/es/table/interface";
import Search from "antd/es/input/Search";
import Highlighter from "react-highlight-words";
import dayjs, { Dayjs } from "dayjs";

//Components..............................................
import { BASE_URL } from "../../../utils/constants";
import {
  FirebaseAppVariant,
  JsonSchemaItem,
  JsonSchemaListResponse,
  MobileNotification,
  MobileNotificationStatusType,
  MobileNotificationType,
  VehicleItem,
  VehicleItemIndex,
} from "../../../utils/interfaces";

import AddVehicleModal from "../../../Components/VehicleConfigPageComp/Modals/AddVehicleModal";
import EditWiringPdfModal from "../../../Components/VehicleConfigPageComp/Modals/EditWiringPdfModal";
import EditVehicleConfigModal from "../../../Components/VehicleConfigPageComp/Modals/EditVehicleConfigModal";
import EditVehicleModal from "../../../Components/VehicleConfigPageComp/Modals/EditVehicleModal";

import CommentsComponent from "../../../Components/CommentsComponent";
import DeleteModal from "../../GlobalComponents/Modals/DeleteModal";
import MigrateAllModal from "../Modals/MigrateAllModal";
import styles from '../../../global.module.css'
import { useForm } from "react-hook-form";
import MobileNotificationSendModal from "../../GlobalComponents/Modals/SendMobileNotificationModal";
import { useHasAction } from "../../../hooks/useHasAction";

// Lazily-loaded pages: /get_vehicles_version_1?offset=&limit= returns one
// batch plus the total row count (same on every page, used to know when
// there's nothing left to fetch).
type VehiclePage = {
  items: VehicleItem[];
  total: number;
};

const VEHICLE_PAGE_SIZE = 100;

// Extracts the numeric part of a schema name (e.g. "CONF 2.0" -> 2) so
// versions can be compared to find the latest one, regardless of how many
// of them exist.
const getConfVersionNumber = (versionSchemaName: string): number => {
  const match = versionSchemaName.match(/(\d+(?:\.\d+)?)/);
  return match ? parseFloat(match[1]) : -Infinity;
};

const VehicleListView: React.FC = () => {
  const emptyCar: VehicleItem = {
    json_config: {},
    created_at: null,
    date_released: null,
    json_config_versions: null,
    id: 0,
    manufacturer: "",
    model: "",
    type: "",
    wiring_instruction: null,
    year_start: "",
    year_end: "",
    status: "",
    notification_status: MobileNotificationStatusType.NO_NOTIFICATION
  };

  const canAddVehicle: boolean = useHasAction("vehicle_config_add_vehicle");
  const canDeleteVehicle: boolean = useHasAction("vehicle_config_delete_vehicle");
  const canEditVehicle: boolean = useHasAction("vehicle_config_edit_vehicle");
  const canSendMobileNotificationVehicleConfig: boolean = useHasAction("vehicle_config_send_mobile_notification");
  const canDownloadInstructionsVehicleConfig: boolean = useHasAction("vehicle_config_download_instructions");
  const canUploadInstructionsVehicleConfig: boolean = useHasAction("vehicle_config_upload_instructions");
  const canDownloadConfigBinary: boolean = useHasAction("vehicle_config_download_binary");
  const canEditConfig: boolean = useHasAction("vehicle_config_edit_config");

  const [selectedVehicle, setSelectedVehicle] = useState<VehicleItem>(emptyCar);
  const [selectedJsonSchemaId, setSelectedJsonSchemaId] = useState<number>(0);
  const [selectedVehicleConfigId, setSelectedVehicleConfigId] = useState<number>(0);

  const [showAddVehicleModal, setShowAddVehicleModal] = useState<boolean>(false);
  const [showEditVehicleModal, setShowEditVehicleModal] = useState<boolean>(false);
  const [showSendNotificationModal, setShowSendNotificationModal] = useState<boolean>(false);
  const [sendingMobileNotification, setSendingMobileNotification] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showMigrateModal, setShowMigrateModal] = useState<boolean>(false);
  const [showPdfEditModal, setShowPdfEditModal] = useState<boolean>(false);
  const [showVehicleJsonEditModal, setShowVehicleJsonEditModal] = useState<boolean>(false);

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(0);

  const [migratingTest, setMigratingTest] = useState(false);
  const [migratingRelease, setMigratingRelease] = useState(false);
  const [migrateProgress, setMigrateProgress] = useState<{ completed: number; total: number }>({ completed: 0, total: 0 });
  // Each CONF version migrates from the one before it.
  const MIGRATION_SOURCE: Record<string, string> = { "CONF 2.0": "CONF 1.0", "CONF 3.0": "CONF 2.0" };
  const [migratePair, setMigratePair] = useState<{ from: string; to: string }>({ from: "CONF 1.0", to: "CONF 2.0" });

  const [api, contextHolder] = notification.useNotification();

  // antd's virtual Table needs a concrete pixel height (rc-virtual-list uses
  // it to compute how many rows to render) - a CSS `calc()` string works for
  // a plain scroll container but not here, so track the equivalent number.
  const [tableScrollY, setTableScrollY] = useState(() => window.innerHeight - 255);
  useEffect(() => {
    const onResize = () => setTableScrollY(window.innerHeight - 255);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const {
    control: sendMobileNotificationControl,
    handleSubmit: sendMobileNotificationHandleSubmit,
    formState: { errors: sendMobileNotificationErrors, isValid: sendMobileNotificationIsValid },
    reset: sendMobileNotificationReset
  } = useForm<MobileNotification>({mode: "onChange"});

  const vehicleListQuery = useInfiniteQuery({
    queryKey: ["vehicleList"],
    queryFn: async ({ pageParam }) => {
      const res = await axios.get<{ success: boolean; content: VehiclePage }>(
        `${BASE_URL}get_vehicles_version_1`,
        { params: { offset: pageParam, limit: VEHICLE_PAGE_SIZE } }
      );
      return res.data.content;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.items.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
  });

  const vehicles = useMemo(
    () => vehicleListQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [vehicleListQuery.data]
  );
  const totalVehicleCount = vehicleListQuery.data?.pages[0]?.total ?? null;
  const stillLoadingMore = vehicleListQuery.hasNextPage || vehicleListQuery.isFetchingNextPage;

  // Tracks whether any column filter/search or sort is currently applied -
  // antd's Table reports this via onChange regardless of which mechanism
  // (filterDropdown search box or a plain `filters` list) drove it.
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [activeSorters, setActiveSorters] = useState<SorterResult<VehicleItem>[]>([]);
  const hasActiveFilterOrSort =
    Object.values(activeFilters).some((value) => Array.isArray(value) && value.length > 0) ||
    activeSorters.some((sorter) => !!sorter.order);
  // While a filter/sort is applied and the rest of the data is still being
  // force-loaded (see the effect below), show the table's own overlay
  // spinner rather than the small footer one - "everything's still
  // loading", not "more is available if you scroll".
  const isForceLoadingForFilterOrSort = hasActiveFilterOrSort && stillLoadingMore;

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: SorterResult<VehicleItem> | SorterResult<VehicleItem>[]
  ) => {
    setActiveFilters(filters);
    setActiveSorters(Array.isArray(sorter) ? sorter : [sorter]);
  };

  // A filter/sort can only be trusted once every row has been loaded -
  // otherwise it'd silently operate on whatever partial data happened to
  // be loaded so far. As soon as one is applied, keep pulling pages in the
  // background (each fetchNextPage() re-triggers this effect once it
  // resolves) until nothing's left, rather than waiting for more scrolling.
  useEffect(() => {
    if (!hasActiveFilterOrSort) return;
    if (!vehicleListQuery.hasNextPage || vehicleListQuery.isFetchingNextPage) return;
    vehicleListQuery.fetchNextPage();
  }, [hasActiveFilterOrSort, vehicleListQuery.hasNextPage, vehicleListQuery.isFetchingNextPage]);// eslint-disable-line react-hooks/exhaustive-deps

  const handleTableScroll = (e: React.UIEvent<HTMLElement>) => {
    if (hasActiveFilterOrSort) return; // already being force-loaded above
    const target = e.currentTarget;
    const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 200;
    if (nearBottom && vehicleListQuery.hasNextPage && !vehicleListQuery.isFetchingNextPage) {
      vehicleListQuery.fetchNextPage();
    }
  };

  // One color per filterable column - used both for that column's chips in
  // the title bar and for the little dot next to the column's own header,
  // so a chip and the header it came from are visually traceable at a
  // glance. Same scheme as RecordDataView's filter bar.
  const COLUMN_FILTER_COLORS: Record<string, string> = {
    created_at: "#52c41a",
    manufacturer: "#1677ff",
    model: "#722ed1",
    type: "#13c2c2",
    wiring_instruction: "#fa8c16",
  };

  const COLUMN_FILTER_LABELS: Record<string, string> = {
    created_at: "Created at",
    manufacturer: "Manufacturer",
    model: "Model",
    type: "Type",
    wiring_instruction: "Wiring PDF",
  };

  const WIRING_PDF_FILTER_LABELS: Record<string, string> = {
    yes: "Has PDF",
    no: "No PDF",
  };

  // "Created at" packs its whole range into one selectedKeys entry
  // ("startISO|endISO") rather than two separate values - see
  // getCreatedAtFilterProps below for why. Declared here (rather than next
  // to getCreatedAtFilterProps) since filterChips, below, needs it too.
  const CREATED_AT_RANGE_SEPARATOR = "|";

  const parseCreatedAtRangeKey = (raw: unknown): [Dayjs, Dayjs] | null => {
    if (typeof raw !== "string" || !raw.includes(CREATED_AT_RANGE_SEPARATOR)) return null;
    const [start, end] = raw.split(CREATED_AT_RANGE_SEPARATOR);
    const startDate = dayjs(start);
    const endDate = dayjs(end);
    return startDate.isValid() && endDate.isValid() ? [startDate, endDate] : null;
  };

  // Small colored dot appended to a column's title while it has an active
  // filter - same color as that column's chips in the title bar.
  const renderColumnTitle = (key: string, label: string) => (
    <Space size={6}>
      <span>{label}</span>
      {Array.isArray(activeFilters[key]) && activeFilters[key].length > 0 && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            display: "inline-block",
            background: COLUMN_FILTER_COLORS[key],
          }}
        />
      )}
    </Space>
  );

  // Drops one value from one column's filter, leaving any other selected
  // values on that same column untouched.
  const removeFilterValue = (key: string, value: any) => {
    setActiveFilters((prev) => {
      const current = prev[key];
      if (!Array.isArray(current)) return prev;
      const next = current.filter((v) => v !== value);
      const updated = { ...prev };
      if (next.length > 0) {
        updated[key] = next;
      } else {
        delete updated[key];
      }
      return updated;
    });
    if (searchedColumn === key) {
      setSearchText("");
      setSearchedColumn("");
    }
  };

  // Every currently applied column filter, as one flat list of removable
  // chips - shown in the card title (see the filter-chips row below).
  const filterChips = useMemo(() => {
    const chips: { key: string; label: string; color: string; onRemove: () => void }[] = [];
    Object.entries(activeFilters).forEach(([key, values]) => {
      if (!Array.isArray(values)) return;
      values.forEach((value) => {
        const range = key === "created_at" ? parseCreatedAtRangeKey(value) : null;
        const displayValue = range
          ? `${range[0].format("YYYY-MM-DD")} → ${range[1].format("YYYY-MM-DD")}`
          : key === "wiring_instruction"
          ? (WIRING_PDF_FILTER_LABELS[value] ?? value)
          : value;
        chips.push({
          key: `${key}-${value}`,
          label: `${COLUMN_FILTER_LABELS[key] ?? key}: ${displayValue}`,
          color: COLUMN_FILTER_COLORS[key] ?? "default",
          onRemove: () => removeFilterValue(key, value),
        });
      });
    });
    return chips;
  }, [activeFilters, searchedColumn]);// eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () => {
    setActiveFilters({});
    setSearchText("");
    setSearchedColumn("");
  };

  const jsonSchemaListQuery = useQuery({
    queryKey: ["jsonSchemaList"],
    queryFn: async () => {
      const res = await axios.get<JsonSchemaListResponse>(
        `${BASE_URL}/get_json_schemas_base`
      );
      return res.data.content;
    },
  });

  // The newest CONF version's column can never be hidden - only older ones
  // can be toggled off. `null` here means "no manual choices yet", so the
  // default (latest only) applies; once the admin toggles anything, this
  // becomes the explicit set of extra versions shown alongside the latest.
  const latestSchemaName = useMemo(() => {
    if (!jsonSchemaListQuery.data || jsonSchemaListQuery.data.length === 0) return null;
    return jsonSchemaListQuery.data.reduce((latest: JsonSchemaItem, schema: JsonSchemaItem) =>
      getConfVersionNumber(schema.version_schema_name) > getConfVersionNumber(latest.version_schema_name)
        ? schema
        : latest
    ).version_schema_name;
  }, [jsonSchemaListQuery.data]);

  const [manuallyVisibleSchemas, setManuallyVisibleSchemas] = useState<string[] | null>(null);

  const visibleSchemaNames = useMemo(() => {
    const extra = manuallyVisibleSchemas ?? [];
    return new Set(latestSchemaName ? [latestSchemaName, ...extra] : extra);
  }, [latestSchemaName, manuallyVisibleSchemas]);

  const toggleSchemaVisibility = (versionSchemaName: string) => {
    if (versionSchemaName === latestSchemaName) return;
    setManuallyVisibleSchemas((prev) => {
      const current = new Set(prev ?? []);
      if (current.has(versionSchemaName)) {
        current.delete(versionSchemaName);
      } else {
        current.add(versionSchemaName);
      }
      return Array.from(current);
    });
  };

  const openCommentModal = (vehicle: VehicleItem) => {
    setSelectedVehicle(vehicle);
    setSelectedVehicleId(vehicle.id);
    setShowCommentModal(true);
  };
  const closeCommentModal = () => {
    setShowCommentModal(false);
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: VehicleItemIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters: () => void, close: () => void) => {
    clearFilters();
    setSearchText("");

    close();
  };
  const handleEdit = (vehicle: VehicleItem) => {
    setSelectedVehicle(vehicle);
    setShowEditVehicleModal(true);
  };
  const handleSendNotification = (vehicle: VehicleItem) => {
    const defaultTitle = `${vehicle.manufacturer} ${vehicle.model} ${vehicle.year_start === vehicle.year_end ? `${vehicle.year_start}` : `${vehicle.year_start} - ${vehicle.year_end}`} -> New config available (HB, PL)`;
    // const defaultBody = `New configuration available for ${vehicle.manufacturer} ${vehicle.model} ${vehicle.type ?? ''} ${vehicle.year_start === vehicle.year_end ? `${vehicle.year_start}` : `${vehicle.year_start} - ${vehicle.year_end}`}`;
    const defaultBody = `Great news, we've just launched a configuration for the ${vehicle.manufacturer} ${vehicle.model} ${vehicle.year_start === vehicle.year_end ? `${vehicle.year_start}` : `${vehicle.year_start} - ${vehicle.year_end}`}. More vehicles on the way soon!`;


    setSelectedVehicle(vehicle);
    sendMobileNotificationReset({title: defaultTitle, body: defaultBody})
    toggleSendMobileNotificationModal(true);
  };

  const getColumnSearchProps = (
    dataIndex: VehicleItemIndex
  ): TableColumnType<VehicleItem> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Search
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onSearch={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "flex" }}
          allowClear={{
            clearIcon: (
              <CloseOutlined
                onClick={() => clearFilters && handleReset(clearFilters, close)}
              />
            ),
          }}
        />
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <>
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      </>
    ),
    // Controlled so the filter-chips row (see filterChips) can clear this
    // column's search from outside the column's own dropdown.
    filteredValue: activeFilters[dataIndex as string] ?? null,
    onFilter: (value, record) => {
      // @ts-ignore
      const fieldValue = record[dataIndex];
      return fieldValue != null && fieldValue
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  // "Created at" range filter: unlike the text columns above, a range needs
  // its two bounds evaluated together (AND), not antd's usual "row passes
  // if it matches ANY selected value" (OR) semantics for multi-value
  // filters - so the whole range is packed into a single selectedKeys
  // entry ("startISO|endISO") and unpacked again in onFilter/filterChips,
  // rather than using two separate selectedKeys values. (CREATED_AT_RANGE_SEPARATOR/
  // parseCreatedAtRangeKey themselves live earlier in the component, next to
  // COLUMN_FILTER_LABELS, since filterChips - defined before this point -
  // needs them too.)
  const getCreatedAtFilterProps = (): TableColumnType<VehicleItem> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      const range = parseCreatedAtRangeKey(selectedKeys[0]);
      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <DatePicker.RangePicker
            value={range}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                const startKey = dates[0].startOf("day").toISOString();
                const endKey = dates[1].endOf("day").toISOString();
                setSelectedKeys([`${startKey}${CREATED_AT_RANGE_SEPARATOR}${endKey}`]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ marginBottom: 8, display: "flex" }}
          />
          <Space>
            <Button type="primary" size="small" style={{ width: 90 }} onClick={() => confirm()}>
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <CalendarOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    filteredValue: activeFilters.created_at ?? null,
    onFilter: (value, record) => {
      const range = parseCreatedAtRangeKey(value);
      if (!range || !record.created_at) return false;
      const created = dayjs(record.created_at as unknown as string);
      return created.isValid() && !created.isBefore(range[0]) && !created.isAfter(range[1]);
    },
  });

  const getJsonConfigColumns = (): TableColumnsType<VehicleItem> => {
    var columns: TableColumnsType<VehicleItem> = [];
    jsonSchemaListQuery.data
      ?.filter((jsonSchema: JsonSchemaItem) => visibleSchemaNames.has(jsonSchema.version_schema_name))
      .forEach((jsonSchema: JsonSchemaListResponse["content"][number]) => {
      const migrationSource = MIGRATION_SOURCE[jsonSchema.version_schema_name];

      columns?.push({
        title: (<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {jsonSchema.version_schema_name}
          {migrationSource && (
            <Tooltip title={`Migrate all ${migrationSource}`}>
              <Button
                type="dashed"
                size="small"
                icon={<UpSquareOutlined />}
                onClick={() => {
                  setMigratePair({ from: migrationSource, to: jsonSchema.version_schema_name });
                  setShowMigrateModal(true);
                }}
              />
            </Tooltip>
          )}
        </div>),
        key: jsonSchema.version_schema_name.trim().toLocaleLowerCase(),
        render: (_text: any, vehicle: VehicleItem) => {
          const testValid = !!vehicle.json_config_versions![
            jsonSchema.version_schema_name
          ]?.TEST.valid;
          const releaseValid = !!vehicle.json_config_versions![
            jsonSchema.version_schema_name
          ]?.RELEASE.valid;
          const validStyle: CSSProperties = { backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" };
          return (
            <Space>
              <Tooltip title={testValid ? "Valid TEST config" : "Invalid TEST config"}>
                <Button
                  size="small"
                  danger={!testValid}
                  style={testValid ? validStyle : undefined}
                  disabled={!canEditConfig && !canDownloadConfigBinary}
                  onClick={() => {
                    setSelectedJsonSchemaId(jsonSchema.id);
                    setSelectedVehicleConfigId(
                      vehicle.json_config_versions![
                        jsonSchema.version_schema_name
                      ].TEST.config_id
                    );
                    setSelectedVehicle(vehicle);
                    setShowVehicleJsonEditModal(!showVehicleJsonEditModal);
                  }}
                >
                  TEST
                </Button>
              </Tooltip>
              <Tooltip title={releaseValid ? "Valid RELEASE config" : "Invalid RELEASE config"}>
                <Button
                  size="small"
                  danger={!releaseValid}
                  style={releaseValid ? validStyle : undefined}
                  disabled={!canEditConfig && !canDownloadConfigBinary}
                  onClick={() => {
                    setSelectedJsonSchemaId(jsonSchema.id);
                    setSelectedVehicleConfigId(
                      vehicle.json_config_versions![
                        jsonSchema.version_schema_name
                      ].RELEASE.config_id
                    );
                    setSelectedVehicle(vehicle);
                    setShowVehicleJsonEditModal(!showVehicleJsonEditModal);
                  }}
                >
                  RELEASE
                </Button>
              </Tooltip>
            </Space>
          );
        },
        width: 130,
      });
    });
    return columns;
  };

  getJsonConfigColumns();

  // Columns definition for Ant Design Table
  const columns: TableColumnsType<VehicleItem> = [
    {
      title: renderColumnTitle("created_at", "Created at"),
      dataIndex: "created_at",
      key: "created_at",
      // @ts-ignore
      sorter: (a: { created_at: string | null }, b: { created_at: null }) => {
        if (a.created_at !== null && b.created_at !== null) {
          return a.created_at.localeCompare(b.created_at);
        } else {
          return 0;
        }
      },
      ...getCreatedAtFilterProps(),
      width: 200,
    },
    {
      title: renderColumnTitle("manufacturer", "Manufacturer"),
      dataIndex: "manufacturer",
      key: "manufacturer",
      ...getColumnSearchProps("manufacturer"),
      width: 200,
      fixed: "left" as const,
    },
    {
      title: renderColumnTitle("model", "Model"),
      dataIndex: "model",
      key: "model",
      ...getColumnSearchProps("model"),
      width: 200,
      fixed: "left" as const,
    },
    {
      title: renderColumnTitle("type", "Type"),
      dataIndex: "type",
      key: "type",
      ...getColumnSearchProps("type"),
      width: 200,
    },
    {
      title: "Year",
      key: "year",
      render: (_text: any, record: VehicleItem) => {
        if(record.year_start === record.year_end){
          return <span>{record.year_start}</span>
        } else  {
          return <span>{record.year_start} - {record.year_end}</span>
        }
      },
      width: 100,
      fixed: "left" as const,
    },
    {
      title: renderColumnTitle("wiring_instruction", "Wiring PDF"),
      dataIndex: "wiring_instruction",
      key: "wiring_instruction",
      filters: [
        { text: "Has PDF", value: "yes" },
        { text: "No PDF", value: "no" },
      ],
      filteredValue: activeFilters.wiring_instruction ?? null,
      onFilter: (value, record) =>
        value === "yes" ? !!record.wiring_instruction : !record.wiring_instruction,
      render: (_text: any, record: VehicleItem) => (
        <Tooltip title={record.wiring_instruction ? "Wiring instructions uploaded" : "No wiring instructions uploaded"}>
          <Button
            type={record.wiring_instruction ? "default" : "dashed"}
            icon={<FilePdfOutlined style={{ color: record.wiring_instruction ? "#f5222d" : undefined }} />}
            disabled={!canDownloadInstructionsVehicleConfig && !canUploadInstructionsVehicleConfig}
            onClick={() => {
              setSelectedVehicle(record);
              setShowPdfEditModal(!showPdfEditModal);
            }}
          >
            {record.wiring_instruction ? "Uploaded" : "Missing"}
          </Button>
        </Tooltip>
      ),
      width: 150,
    },
    ...getJsonConfigColumns(),
    {
      title: "Actions",
      key: "actions",
      render: (_text: any, record: VehicleItem) => {
        const notificationMeta = {
          [MobileNotificationStatusType.NO_NOTIFICATION]: { icon: <SendOutlined />, tooltip: "Send notification" },
          [MobileNotificationStatusType.PENDING]: { icon: <ClockCircleOutlined />, tooltip: "Notification pending" },
          [MobileNotificationStatusType.SENT]: { icon: <CheckCircleOutlined />, tooltip: "Notification already sent" },
        }[record.notification_status];
        return (
          <Space wrap>
            <Tooltip title={notificationMeta.tooltip}>
              <Button
                icon={notificationMeta.icon}
                disabled={record.notification_status === MobileNotificationStatusType.SENT || !canSendMobileNotificationVehicleConfig}
                onClick={() => handleSendNotification(record)}
              >
                Send
              </Button>
            </Tooltip>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={!canEditVehicle}
            >
              Edit
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCar(record)}
              disabled={!canDeleteVehicle}
            >
              Delete
            </Button>
            <Tooltip title="View Comments">
              <Button
                icon={<CommentOutlined />}
                onClick={() => openCommentModal(record)}
              />
            </Tooltip>
          </Space>
        );
      },
      fixed: "right" as const,
      width: 300,
    },
  ];

  const toggleSendMobileNotificationModal = (makeVisible: boolean) => {
    if(makeVisible){
      setShowSendNotificationModal(true);
    } else {
      setShowSendNotificationModal(false);
    }
  }

  const handleDeleteCar = (vehicle: VehicleItem) => {
    setSelectedVehicle(vehicle);
    setShowDeleteModal(true);
  };

  const confirmDeleteCar = (vehicle: VehicleItem) => {
    const carID = vehicle.id;
    setShowDeleteModal(false);
    // Handle delete action, you can console log or implement your logic here
    console.log("Delete Car with ID:", carID);
    axios
      .post(`${BASE_URL}/delete_vehicle/${carID}`)
      .then((response) => {
        vehicleListQuery.refetch();
        api.success({
          title: "Vehicle Deleted",
          description: `The vehicle "${vehicle.manufacturer} ${vehicle.model}" was deleted successfully.`,
          placement: "bottomRight",
          showProgress: true
        });
      })
      .catch((error) => {
        console.error("Error fetching Canny device:", error);
        api.error({
          title: "Error",
          description: error.message,
          placement: "bottomRight",
          showProgress: true
        });
      });
  };

  const queryClient = useQueryClient();
  const migrateJsonMutation = useMutation({
    mutationFn: (values: { config_id: number; migrated_json: any }) => {
      return axios.post(`${BASE_URL}/update_json_config/${values.config_id}`, values, {
        headers: { "Content-Type": "application/json" },
      });
    },
    // No per-item invalidation here on purpose: migrateConfigs calls this
    // mutation once per vehicle, and invalidating "vehicleList" on every
    // single success fires that many refetches of /get_vehicles_version_1
    // in a row (worse right near the end, as the concurrency-limited queue
    // drains and several finish close together). Invalidate once after the
    // whole batch settles instead.
  });

  // Runs `worker` over `items` with at most `limit` requests in flight at
  // once, instead of either firing everything at once (races the Flask
  // filesystem session store under the shared session cookie - see the
  // comment in migrateConfigs) or fully serially (correct but slow for a
  // large fleet). Each finished worker immediately grabs the next queued
  // item rather than waiting on batch boundaries, so throughput stays close
  // to `limit`x even when individual requests vary in duration.
  const runWithConcurrencyLimit = async <T,>(
    items: T[],
    limit: number,
    worker: (item: T) => Promise<void>
  ) => {
    let nextIndex = 0;
    const runNext = async (): Promise<void> => {
      const currentIndex = nextIndex;
      nextIndex += 1;
      if (currentIndex >= items.length) return;
      await worker(items[currentIndex]);
      await runNext();
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => runNext()));
  };

  const migrateConfigs = ( status:String) => {
    if(status === 'RELEASE'){
      setMigratingRelease(true);
    } else {
      setMigratingTest(true);
    }
    try{
      axios
        .get(`${BASE_URL}/migrate_conf/${migratePair.from}/${migratePair.to}/${status}`)
        .then(async (response) => {
          if (response.data.content) {
            const items = response.data.content as { config_id: number; migrated_json: any }[];
            setMigrateProgress({ completed: 0, total: items.length });
            let failedCount = 0;
            // Concurrency of 4 is a deliberate middle ground: high enough to
            // be meaningfully faster than one-at-a-time, low enough to keep
            // simultaneous session-file writes rare (see runWithConcurrencyLimit).
            await runWithConcurrencyLimit(items, 4, async (item) => {
              try {
                await migrateJsonMutation.mutateAsync(item);
              } catch (itemError) {
                failedCount += 1;
                console.error(`Error migrating config ${item.config_id}:`, itemError);
              } finally {
                setMigrateProgress((prev) => ({ ...prev, completed: prev.completed + 1 }));
              }
            });
            await queryClient.invalidateQueries({ queryKey: ["vehicleList"] });
            if (failedCount === 0) {
              api.success({
                title: 'Success',
                description: `You successfully migrated all valid ${status} ${migratePair.from}!`,
                placement: "bottomRight",
                showProgress: true
              });
            } else {
              api.warning({
                title: 'Migration finished with errors',
                description: `${failedCount} config(s) failed to migrate. Check the console and retry if needed.`,
                placement: "bottomRight",
                showProgress: true
              });
            }
          }
        })
        .finally(() => {
          setShowMigrateModal(false)
          setMigratingRelease(false);
          setMigratingTest(false);
          setMigrateProgress({ completed: 0, total: 0 });
        })
    } catch(error){
      api.error({
        title: `Error migrating ${status} ${migratePair.from}`,
        description: (error as any).message,
        placement: "bottomRight",
        showProgress: true
      });
    }
  };

  const handleCreateSendMobileNotification = async (data: MobileNotification, appVariant: FirebaseAppVariant) => {
    setSendingMobileNotification(true);
    try {
      await axios.post(`${BASE_URL}send_vehicle_config_notification`,{
        title: data.title,
        body: data.body,
        appVariant: appVariant,
        vehicle_id: selectedVehicle.id
      });
      api.success({
        title: 'Sending Successful',
        description: `Mobile Notification was sent successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } catch (err) {
      api.error({
        title: 'Sending Failed',
        description: `Failed to send Mobile notification.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setSendingMobileNotification(false);
      toggleSendMobileNotificationModal(false);
      vehicleListQuery.refetch();
    }
  };

  return (
    <div>
      {contextHolder}
      <div>
        <Card
          className={styles.globalCard}
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <span style={{ flexShrink: 0 }}>Vehicle Management</span>
              {filterChips.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1, padding: "2px 0" }}>
                    {filterChips.map((chip) => (
                      <Tag
                        key={chip.key}
                        color={chip.color}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          chip.onRemove();
                        }}
                        style={{ flexShrink: 0 }}
                      >
                        {chip.label}
                      </Tag>
                    ))}
                  </div>
                  <Button size="small" type="link" onClick={clearAllFilters} style={{ flexShrink: 0 }}>
                    Clear all
                  </Button>
                </div>
              )}
              <Space style={{ flexShrink: 0, marginLeft: filterChips.length > 0 ? 0 : "auto" }}>
                <Popover
                  trigger="click"
                  placement="bottomRight"
                  title="Config Versions"
                  content={
                    <Space orientation="vertical">
                      {(jsonSchemaListQuery.data ?? [])
                        .slice()
                        .sort((a: JsonSchemaItem, b: JsonSchemaItem) => getConfVersionNumber(b.version_schema_name) - getConfVersionNumber(a.version_schema_name))
                        .map((schema: JsonSchemaItem) => {
                          const isLatest = schema.version_schema_name === latestSchemaName;
                          return (
                            <Checkbox
                              key={schema.version_schema_name}
                              checked={visibleSchemaNames.has(schema.version_schema_name)}
                              disabled={isLatest}
                              onChange={() => toggleSchemaVisibility(schema.version_schema_name)}
                            >
                              {schema.version_schema_name}
                              {isLatest && (
                                <Typography.Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                                  (latest, always shown)
                                </Typography.Text>
                              )}
                            </Checkbox>
                          );
                        })}
                    </Space>
                  }
                >
                  <Button icon={<SettingOutlined />}>Config Versions</Button>
                </Popover>
                <Tooltip title="Add New Vehicle">
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<PlusOutlined />}
                    disabled={!canAddVehicle}
                    onClick={() => {
                      setShowAddVehicleModal(!showAddVehicleModal);
                    }}
                  />
                </Tooltip>
              </Space>
            </div>
          }
        >
          <Table
            columns={columns}
            dataSource={vehicles}
            loading={vehicleListQuery.isLoading || isForceLoadingForFilterOrSort}
            scroll={{x : 'max-content', y: tableScrollY}}
            virtual
            pagination={false}
            rowKey="id"
            className='horizontally-synced-table'
            onChange={handleTableChange}
            onScroll={handleTableScroll}
            footer={
              !hasActiveFilterOrSort && vehicleListQuery.isFetchingNextPage
                ? () => (
                    <div style={{ textAlign: "center" }}>
                      <Spin size="small" /> Loading more vehicles ({vehicles.length}
                      {totalVehicleCount !== null ? ` / ${totalVehicleCount}` : ""})...
                    </div>
                  )
                : undefined
            }
          />
        </Card>
      </div>
      <CommentsComponent
        showModal={showCommentModal}
        onClose={closeCommentModal}
        commentTarget={"VEHICLE"}
        targetId={selectedVehicleId}
        targetObject={selectedVehicle}
        api={api}
      />
      <AddVehicleModal
        showModal={showAddVehicleModal}
        onClose={() => setShowAddVehicleModal(!showAddVehicleModal)}
        api={api}
      />
      <EditWiringPdfModal
        vehicleId={selectedVehicle.id}
        showModal={showPdfEditModal}
        onClose={() => setShowPdfEditModal(!showPdfEditModal)}
        api={api}
      />
      <EditVehicleConfigModal
        vehicle_name={`${selectedVehicle.manufacturer}
          ${selectedVehicle.model}
          ${selectedVehicle.type ?? ""}
          ${selectedVehicle.year_start === selectedVehicle.year_end ? selectedVehicle.year_start : `${selectedVehicle.year_start} - ${selectedVehicle.year_end}`}`
        }
        vehicle = {selectedVehicle}
        schema_id={selectedJsonSchemaId}
        config_id={selectedVehicleConfigId}
        showModal={showVehicleJsonEditModal}
        onClose={(loading:boolean) => {
          if(!loading){
            setSelectedVehicleConfigId(0);
            setSelectedJsonSchemaId(0);
            setShowVehicleJsonEditModal(!showVehicleJsonEditModal);
          }
        }}
        api={api}
      />
      <EditVehicleModal
        showModal={showEditVehicleModal}
        vehicle={selectedVehicle}
        onClose={() => setShowEditVehicleModal(false)}
        api={api}
      />
      <DeleteModal
        showModal={showDeleteModal}
        onDelete={() => confirmDeleteCar(selectedVehicle)}
        onClose={() => setShowDeleteModal(false)}
      />
      <MigrateAllModal
        showMigrateModal={showMigrateModal}
        setShowMigrateModal={setShowMigrateModal}
        migratingTest={migratingTest}
        migratingRelease={migratingRelease}
        migrateConfigs={migrateConfigs}
        confFrom={migratePair.from}
        confTo={migratePair.to}
        migrateProgress={migrateProgress}
      />
      <MobileNotificationSendModal
        showSendMobileNotificationModal={showSendNotificationModal}
        toggleSendMobileNotificationModal={toggleSendMobileNotificationModal}
        handleSubmit={sendMobileNotificationHandleSubmit}
        errors={sendMobileNotificationErrors}
        onSubmit={handleCreateSendMobileNotification}
        control={sendMobileNotificationControl}
        isValid={sendMobileNotificationIsValid}
        isSubmitting={sendingMobileNotification}
        notificationTopic={MobileNotificationType.NEW_CONFIG_AVAILABLE}
      />
    </div>
  );
};

export default VehicleListView;
